import { analytics } from "@/content";
import { Rich } from "@/lib/rich";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function AnalyticsHowItWorks() {
  const { howItWorks } = analytics;
  const { ref: headerRef, revealed: headerRevealed } = useScrollReveal();

  return (
    <section id="how-it-works" className="py-24 bg-slate-50 dark:bg-[#0C0C0E] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[8%] right-[-5%] w-[500px] h-[500px] rounded-full bg-blue-100/30 dark:bg-blue-500/[0.10] blur-[140px]" />
        <div className="absolute bottom-[5%] left-[-5%] w-[400px] h-[400px] rounded-full bg-slate-200/30 dark:bg-white/[0.04] blur-[130px]" />
        <div className="absolute top-[50%] right-[20%] w-[350px] h-[350px] rounded-full bg-blue-100/25 dark:bg-blue-500/[0.08] blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-700 ${
            headerRevealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.09] text-slate-500 dark:text-white/60 text-xs font-semibold tracking-widest uppercase mb-6 shadow-sm">
            {howItWorks.badge}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight mb-4">
            <Rich text={howItWorks.title} accentClassName="text-blue-600 dark:text-blue-400" />
          </h2>
          {howItWorks.subtitle && (
            <p className="text-slate-600 dark:text-white/65 text-lg leading-relaxed max-w-2xl mx-auto">
              {howItWorks.subtitle}
            </p>
          )}
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-[72px] left-[12%] right-[12%] h-px bg-gradient-to-r from-slate-200 via-blue-300 to-slate-200" />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {howItWorks.steps.map((step, index) => (
              <StepCard key={step.num} step={step} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function StepCard({
  step,
  index,
}: {
  step: { num: string; icon: string; title: string; text: string; tags: string[] };
  index: number;
}) {
  const { ref, revealed } = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`group relative flex flex-col items-center text-center transition-all duration-700 hover:-translate-y-1 cursor-default ${
        revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.09] shadow-sm mb-5 relative z-10 transition-all duration-300 group-hover:scale-110 group-hover:shadow-md group-hover:shadow-blue-500/10 group-hover:border-blue-300">
        <i
          className={`${step.icon} text-blue-600 dark:text-blue-400 text-2xl transition-transform duration-300 group-hover:scale-110`}
        ></i>
      </div>
      <span className="text-blue-500 font-extrabold text-2xl mb-2">{step.num}</span>
      <h4 className="text-slate-900 dark:text-white font-bold text-base mb-2">{step.title}</h4>
      <p className="text-slate-600 dark:text-white/65 text-sm leading-relaxed mb-4">{step.text}</p>
      <div className="flex flex-wrap gap-1.5 justify-center">
        {step.tags.map((tag) => (
          <span
            key={tag}
            className="px-2.5 py-0.5 rounded-full bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.09] text-slate-500 dark:text-white/60 text-[11px] font-medium"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}
